import { PageHeader , Button , Card, CardContent , Input , Label , Skeleton } from "@roberttamas/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

import { Save } from "@/components/icons";
import { LanguageCombobox } from "@/components/language-combobox";
import {
  TranslationImportFeedbackMessage,
  TranslationKeysForm,
  type TranslationImportFeedback,
} from "@/components/translation-keys-form";
import { listVariants, saveTranslations } from "@/lib/api";
import { toAppError } from "@/lib/errors";
import { SUPPORTED_LOCALES } from "@/lib/locales";
import { parseTranslationJsonImport } from "@/lib/translation-json-import";
import {
  buildTranslationDetailPath,
  createEmptyTranslationKeys,
  REQUIRED_TRANSLATION_KEYS,
  TRANSLATION_KEYS,
} from "@/lib/translations";
import type { ListVariantsResp } from "@/lib/types";

export function NewTranslationPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const importInputRef = useRef<HTMLInputElement>(null);
  const [variantTag, setVariantTag] = useState("");
  const [keys, setKeys] = useState<Record<string, string>>(() => createEmptyTranslationKeys());
  const [importFeedback, setImportFeedback] = useState<TranslationImportFeedback | null>(null);

  const variantsQuery = useQuery<ListVariantsResp>({
    queryKey: ["variants"],
    queryFn: listVariants,
    staleTime: 60_000,
  });

  const mutation = useMutation({
    mutationFn: () => saveTranslations({ variant: variantTag, keys }),
    onSuccess: async (resp) => {
      toast.success(t("translations.createdToast", { variant: resp.variant }));
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["variants"] }),
        queryClient.invalidateQueries({ queryKey: ["translations", resp.variant] }),
      ]);
      navigate(buildTranslationDetailPath(resp.variant));
    },
    onError: (err) => toast.error(toAppError(err).message),
  });

  const existingVariants = variantsQuery.data?.variants ?? [];
  const localeOptions = SUPPORTED_LOCALES
    .map((locale) => locale.tag)
    .filter((tag) => !existingVariants.includes(tag));

  const codeValid = localeOptions.includes(variantTag);
  const allFilled = TRANSLATION_KEYS.every((key) => keys[key]?.trim());
  const allDefined = localeOptions.length === 0;
  const canSave = codeValid && allFilled && !mutation.isPending;

  async function handleImportFile(file: File) {
    const rawText = await file.text();
    const result = parseTranslationJsonImport(rawText, REQUIRED_TRANSLATION_KEYS);
    if (!result.ok) {
      setImportFeedback(null);
      toast.error(result.message);
      return;
    }

    setKeys(result.keys);
    setImportFeedback({
      fileName: file.name,
      importedCount: result.importedCount,
      unknownKeys: result.unknownKeys,
      missingRequiredKeys: result.missingRequiredKeys,
      invalidValueKeys: result.invalidValueKeys,
    });
    toast.success(
      t("translationImport.imported", { count: result.importedCount, fileName: file.name }),
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow={t("translations.title")}
        backTo="/translations"
        backLabel={t("translations.backToTranslations")}
        title={t("translations.newTranslation")}
      />

      <Card>
        <CardContent className="pt-6 space-y-6">
          {variantsQuery.isLoading && (
            <div className="space-y-3">
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
            </div>
          )}

          {variantsQuery.isError && (
            <p className="text-sm text-destructive">{t("translations.loadLocalesFailed")}</p>
          )}

          {!variantsQuery.isLoading && !variantsQuery.isError && (
            <>
              <div className="space-y-2">
                <Label htmlFor="new-translation-code">{t("translations.languageLabel")}</Label>
                <LanguageCombobox
                  id="new-translation-code"
                  value={variantTag}
                  options={localeOptions}
                  onChange={setVariantTag}
                  emptyText={t("translations.allDefinedEmpty")}
                  disabled={allDefined}
                />
                {variantTag && !codeValid && (
                  <p className="text-sm text-destructive">
                    {t("translations.selectNotYetDefined")}
                  </p>
                )}
                {allDefined && (
                  <p className="text-sm text-muted-foreground">
                    {t("translations.allCreated")}
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Input
                  ref={importInputRef}
                  type="file"
                  accept="application/json,.json"
                  className="hidden"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (!file) {
                      return;
                    }
                    void handleImportFile(file);
                    e.target.value = "";
                  }}
                />
                <Button
                  type="button"
                  variant="outline"
                  className="w-full sm:w-auto"
                  disabled={mutation.isPending || allDefined}
                  onClick={() => importInputRef.current?.click()}
                >
                  {t("translations.uploadJson")}
                </Button>
                <p className="text-xs text-muted-foreground">
                  {t("translations.acceptsNote")}
                </p>
                {importFeedback && <TranslationImportFeedbackMessage feedback={importFeedback} />}
              </div>

              <TranslationKeysForm keys={keys} onChange={setKeys} />

              <div className="flex justify-end">
                <Button
                  className="w-full sm:w-auto"
                  disabled={!canSave || allDefined}
                  onClick={() => mutation.mutate()}
                >
                  {mutation.isPending ? (
                    t("common.saving")
                  ) : (
                    <>
                      <Save className="h-4 w-4" />
                      {t("translations.saveTranslation")}
                    </>
                  )}
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}